import React, { createContext, useCallback, useMemo, useState } from "react";
import { getAllUserLocations, getUserLocationsShapes, getVisibleLocationShapes, updateLocationShape } from "../api/locations";
import { FullLocationsResponse, GeomShape, LocationShapes, MinLocationBase, VisibleLocationShapesInfo } from "../core/global";
import { getItemByKeyValue } from "../helpers/core";

interface UpdateShapeProps {
  uid?: number | string
  color?: string
  title?: string
  hidden?: boolean
}

interface LocationSummary { 
  geoms: GeomShape[], 
  summary: VisibleLocationShapesInfo[], 
  shape: LocationShapes,
  features?: {
    geometry: GeoJSON.Geometry,
    properties?: {[key: string]: any}
    type: string
  }
}

interface LocationsContextProps {
  updateShape: (props: UpdateShapeProps) => void
  locations: FullLocationsResponse[] | null;
  refetchLocations: () => void;
  refetchLocationShapes: (uid: number | null) => void;
  refetchUserVisibleLocations: () => void;
  setCurrentLocation: (uidOrLocation: number | string | null | MinLocationBase) => void
  currentLocation: FullLocationsResponse | null | MinLocationBase
  currentLocationShapes: LocationShapes[] | null
  notSavedYetLocationShapes: LocationShapes[] | null
  visibleLayerLocationShapes: LocationShapes[] | null
  setNotSavedYetLocationShapes: React.Dispatch<React.SetStateAction<LocationShapes[] | null>>
  loading: string[]
  hasLocations: boolean
  noneVisible: boolean
  currentValue: number | null
  setCurrentValue: React.Dispatch<React.SetStateAction<number | null>>
  visibleLocationFullSummary: LocationSummary |  null,
  setVisibleLocationFullSummary: React.Dispatch<React.SetStateAction<LocationSummary | null>> 
  currentShapeUid: number | null
  setCurrentShapeUid: React.Dispatch<React.SetStateAction<number | null>>
  
}

const initialValue: LocationsContextProps = {
  updateShape: () => {},
  currentValue: null,
  setCurrentValue: () => {},
  locations: null,
  refetchLocations: () => {},
  refetchLocationShapes: () => {},
  setCurrentLocation: () => null,
  currentLocation: null,
  currentLocationShapes: null,
  notSavedYetLocationShapes: null,
  setNotSavedYetLocationShapes: () => {},
  visibleLayerLocationShapes: null,
  refetchUserVisibleLocations: () => {},
  loading: [],
  hasLocations: false,
  noneVisible: true,
  visibleLocationFullSummary: null,
  setVisibleLocationFullSummary: () => {},
  setCurrentShapeUid: () => {},
  currentShapeUid: null
};

export const LocationsContext = createContext<LocationsContextProps>(initialValue);

export const newLocation: MinLocationBase = {
  notes: '',
  title: '',
}

const removeLoading = (i: string[], k: string) => {
  return i.filter(d => d !== k)
}

const addLoading = (i: string[], k: string) => {
  return [...i.filter(d => d !== k), k]
}

interface LocationsProviderProps {
  children?: React.ReactNode;
}

export const LocationsProvider = ({ children }: LocationsProviderProps) => {
  const [locations, setLocations] = useState(initialValue.locations);
  const [currentLocation, _setCurrentLocation] = useState(initialValue.currentLocation);
  const [visibleLayerLocationShapes, setVisibleLayerLocationShapes] = useState(initialValue.visibleLayerLocationShapes)
  const [currentLocationShapes, _setCurrentLocationShapes] = useState(initialValue.currentLocationShapes);
  const [notSavedYetLocationShapes, setNotSavedYetLocationShapes] = useState(initialValue.notSavedYetLocationShapes);

  const [loading, setLoading] = useState(initialValue.loading)
  const [currentValue, setCurrentValue] = useState(initialValue.currentValue)
  const [visibleLocationFullSummary, setVisibleLocationFullSummary] = useState(initialValue.visibleLocationFullSummary)
  const [currentShapeUid, setCurrentShapeUid] = useState(initialValue.currentShapeUid)
  
  const hasLocations = useMemo(() => {
    return !!(locations && Array.isArray(locations) && locations.length > 0)
  }, [locations])

  const noneVisible = useMemo(() => {
    if (locations && Array.isArray(locations)) {
      const allVisible = locations.filter( item => item.visible)
      return allVisible.length === 0  
    }
    return true
  }, [locations])

  // either sets location by uid from the list, or takes the obkect and sets it
  // empty object is used for creating new locations
  const setCurrentLocation = useCallback((uidOrLocation: number | string | null | MinLocationBase) => {
    if (uidOrLocation === null) {
      _setCurrentLocation(null)
    } else if (typeof uidOrLocation === 'number' && locations && locations.length > 0) {
      const found = getItemByKeyValue(locations, 'uid', uidOrLocation)
      if (found) {
        _setCurrentLocation(found)
      } else {
        _setCurrentLocation(null)
      }
    } else if (typeof uidOrLocation !== 'number' && typeof uidOrLocation !== 'string') {
      _setCurrentLocation(uidOrLocation)
    }
    
  }, [locations])
  
  const refetchUserVisibleLocations = useCallback(() => {
    const execute = async () => {
      setLoading(i => addLoading(i, 'visible-shapes-info'))
      const {response: locationShapes} = await getVisibleLocationShapes({})

      let locationShapesSorted = locationShapes || null
      if (locationShapes) {
        locationShapesSorted = locationShapes.sort( (a: any, b: any) => {
          const titleA = a.shape_title || ''
          const titleB = b.shape_title || ''
          return titleA.localeCompare(titleB)
        })
      }
      setVisibleLayerLocationShapes(locationShapesSorted)
      setLoading(i => removeLoading(i, 'visible-shapes-info'))
    }
    setVisibleLayerLocationShapes(null)
    setVisibleLocationFullSummary(null)
    execute()
  }, [])

  const refetchLocations = useCallback(() => {
    const execute = async () => {
      const { response } = await getAllUserLocations()
      setLocations(response as FullLocationsResponse[]);
      refetchUserVisibleLocations()
    };
    execute()
  }, [])

  const updateShape = useCallback( ({uid, color, title, hidden}: UpdateShapeProps) => {
    const execute = async () => {
      if (color) {
        setVisibleLocationFullSummary( (item: any) => {
            const {shape} = item || {}
            if (shape?.uid === uid) {
              return {
                ...item,
                shape: {
                  ...shape,
                  color,
                }
              }
            }
            return item
        })
        setVisibleLayerLocationShapes( shapes => {
          return shapes ? shapes.map( shapeItem => {
            if (shapeItem.shape_uid === uid) {
              const {features} = shapeItem
              return {
                ...shapeItem,
                color,
                features: {
                  ...features,
                  properties: {
                    ...features.properties,
                    color,
                  }
                }
              }
            }
            return shapeItem
          }) : null
        })
      }
      
      _setCurrentLocationShapes( shapes => {
        return shapes ? shapes.map( shapeItem => {
          if (shapeItem.uid === uid) {
            return {
              ...shapeItem,
              title: title || shapeItem.title,
              color: color || shapeItem.color,
              hidden: typeof hidden === 'boolean' ? hidden : shapeItem.hidden,
            }
          }
          return shapeItem
        }) : null
      })

      if (notSavedYetLocationShapes) {
        setNotSavedYetLocationShapes( shapes => {
          return shapes ? shapes.map( shapeItem => {
            if (shapeItem.uid === uid) {
              return {
                ...shapeItem,
                title: title || shapeItem.title,
                color: color || shapeItem.color,
                hidden: typeof hidden === 'boolean' ? hidden : shapeItem.hidden,
              }
            }
            return shapeItem
          }) : null
        })
      }

      if (uid) {
        await updateLocationShape({uid, color, title, hidden})
      }
    };
    execute()
  }, [visibleLocationFullSummary, visibleLayerLocationShapes, notSavedYetLocationShapes, currentLocationShapes])

  const refetchLocationShapes = async (uid: number | null) => {
    if (uid) {
      _setCurrentLocationShapes(null);
      setLoading(i => addLoading(i, 'location-shapes'))
      const { response } = await getUserLocationsShapes({uid})
      _setCurrentLocationShapes(response as LocationShapes[]);
      setLoading(i => removeLoading(i, 'location-shapes'))
    } else {
      _setCurrentLocationShapes(null);
    }
  };
  
  return (
    <LocationsContext.Provider
      value={{
        updateShape,
        locations,
        refetchLocations,
        currentLocation,
        setCurrentLocation,
        currentLocationShapes,
        refetchLocationShapes,
        notSavedYetLocationShapes, 
        setNotSavedYetLocationShapes,
        refetchUserVisibleLocations,
        visibleLayerLocationShapes,
        loading,
        hasLocations,
        noneVisible,
        currentValue,
        setCurrentValue,
        currentShapeUid, 
        setCurrentShapeUid,
        visibleLocationFullSummary,
        setVisibleLocationFullSummary,
      }}
    >
      {children}
    </LocationsContext.Provider>
  );
};
